/*
  Customer Reviews JavaScript - Nector.io Integration (Enhanced with Logging)
  Place this file in: assets/customer-reviews.js
*/

class CustomerReviews {
  constructor() {
    this.currentPage = 1;
    this.reviewsPerPage = 3;
    this.allReviews = [];
    this.filteredReviews = [];
    this.currentFilter = 'all';
    this.currentSort = 'image_first';
    this.productId = null;
    this.isInitialized = false;
    this.isFetching = false;

    // Nector API Configuration
    this.nectorConfig = {
      apiKey: 'ak_40a4a9a8bfe0adbe918b0a9e4605a694ab1d8aaece1bc1022dcfe873ee9ca824',
      workspaceId: 'shopify-mme1cp-ti',
      baseUrl: 'https://cachefront.nector.io/api/v2/merchant/reviews'
    };

    this.allReviews = []; // Accumulate all fetched reviews here
    this.nextApiPageToFetch = 1;
    this.apiPageSize = 20; // Default batch size
    this.totalApiReviews = 0; // Total count from server
    this.apiStats = null; // Store stats from API

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    const initReviews = () => {
      const section = document.querySelector('.reviews-section');
      if (!section) {
        console.log('⏳ Reviews section not found, waiting...');
        return false;
      }

      this.productId = section.dataset.productId;
      console.log('📦 Product ID detected:', this.productId);

      if (!this.productId) {
        console.error('❌ Product ID not found in data-product-id attribute');
        this.showNoReviews();
        return true; // Stop retrying
      }

      this.setupEventListeners();
      this.fetchReviews(); // This will now call the new fetch logic
      this.isInitialized = true;
      return true; // Initialized successfully
    };

    if (!initReviews()) {
      // Use MutationObserver to wait for the section
      const observer = new MutationObserver((mutations, obs) => {
        if (initReviews()) {
          obs.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Fallback: retry a few times with delay just in case
      let attempts = 0;
      const maxAttempts = 10;
      const interval = setInterval(() => {
        attempts++;
        if (initReviews() || attempts >= maxAttempts) {
          clearInterval(interval);
          if (attempts >= maxAttempts) console.warn('⚠️ Failed to find reviews section after max attempts');
        }
      }, 500);
    }
  }

  setupEventListeners() {
    // Filter buttons
    const filterTags = document.querySelectorAll('.filter-tag');
    filterTags.forEach(tag => {
      tag.addEventListener('click', (e) => {
        const filter = e.currentTarget.dataset.filter;
        this.handleFilterChange(filter);
      });
    });

    const writeBtns = document.querySelectorAll('.btn-write-review');
    writeBtns.forEach(writeBtn => {
      let touchStartTime = 0;
      let touchStartPos = null;

      // Handle click (works on both desktop and mobile after touch)
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openReviewForm();
      };

      // Handle touchstart - just track it, don't trigger
      writeBtn.addEventListener('touchstart', (e) => {
        touchStartTime = Date.now();
        touchStartPos = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
      }, { passive: true });

      // Handle touchend - check for tap vs scroll
      writeBtn.addEventListener('touchend', (e) => {
        if (!touchStartPos) return;

        const touchEndTime = Date.now();
        const touchEndPos = {
          x: e.changedTouches[0].clientX,
          y: e.changedTouches[0].clientY
        };

        const distance = Math.sqrt(
          Math.pow(touchEndPos.x - touchStartPos.x, 2) +
          Math.pow(touchEndPos.y - touchStartPos.y, 2)
        );

        // If it was a short tap with little movement, treat as click
        if (distance < 10 && (touchEndTime - touchStartTime) < 500) {
          e.preventDefault();
          e.stopPropagation();
          this.openReviewForm();
        }
      });

      // Standard click listener
      writeBtn.addEventListener('click', handleClick);
    });

    const toggle = document.querySelector('.filter-toggle');
    if (toggle) {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const menu = document.querySelector('.filter-menu');
        if (menu && menu.classList.contains('open')) {
          menu.classList.remove('open');
        } else {
          this.openSortMenu();
        }
      });
    }

    document.addEventListener('click', (e) => {
      const menu = document.querySelector('.filter-menu');
      if (menu && !menu.contains(e.target) && !e.target.closest('.filter-toggle')) {
        menu.classList.remove('open');
      }
    });
  }

  async fetchReviews() {
    if (this.isFetching) return;
    this.isFetching = true;

    try {
      this.showLoading();
      console.log('🔄 Initializing reviews fetch...');

      // Reset state
      this.allReviews = [];
      this.nextApiPageToFetch = 1;
      this.currentPage = 1;

      // Fetch enough reviews for the first page
      await this.ensureReviewsForPage(1);

      this.hideLoading();
      this.isFetching = false;

    } catch (error) {
      console.error('❌ Error fetching Nector reviews:', error);
      console.error('Error stack:', error.stack);
      this.hideLoading();
      this.showError(error.message);
      this.isFetching = false;
    }
  }

  // Ensures we have enough valid reviews to display the requested UI page
  async ensureReviewsForPage(uiPage) {
    const targetCount = uiPage * this.reviewsPerPage;

    // Check how many valid reviews we currently have
    let currentValidCount = this.getValidReviewsCount();

    console.log(`🎯 Ensure reviews for page ${uiPage}: need ${targetCount}, have ${currentValidCount}`);

    // If we already have enough, or if we've fetched everything, stop
    if (currentValidCount >= targetCount) {
      console.log('✅ Already have enough reviews');
      this.processReviews(); // Update stats if needed
      this.renderReviews();
      return;
    }

    // Loop to fetch more API pages until we have enough or run out
    let retries = 3;
    while (currentValidCount < targetCount) {
      // If we know total reviews and we've fetched them all, stop
      if (this.totalApiReviews > 0 && this.allReviews.length >= this.totalApiReviews) {
        console.log('⚠️ Fetched all available reviews from API');
        break;
      }

      try {
        console.log(`⬇️ Fetching API page ${this.nextApiPageToFetch} to fill UI...`);
        const data = await this.fetchReviewsPage(this.nextApiPageToFetch);

        if (!data || !data.data || !data.data.items || data.data.items.length === 0) {
          console.log('⚠️ No more items returned from API');
          break;
        }

        // Update total count and stats from first page (or any page)
        if (this.nextApiPageToFetch === 1) {
          this.totalApiReviews = data.data.count || 0;
          this.apiStats = data.data.stats; // Capture stats!
          console.log('📊 Captured API stats:', this.apiStats);
        }

        const newReviews = data.data.items.map((r, i) => this.formatNectorReview(r, this.allReviews.length + i));
        this.allReviews = [...this.allReviews, ...newReviews];
        this.nextApiPageToFetch++;

        // Recalculate valid count
        currentValidCount = this.getValidReviewsCount();
        console.log(`📊 Progress: Have ${currentValidCount} valid reviews (total fetched: ${this.allReviews.length})`);

      } catch (e) {
        console.error(`❌ Error fetching API page ${this.nextApiPageToFetch}:`, e);
        retries--;
        if (retries === 0) break;
        await new Promise(r => setTimeout(r, 1000));
      }
    }

    this.processReviews();
    this.renderReviews();
  }

  getValidReviewsCount() {
    // Count how many reviews match current filter and are not hidden
    return this.allReviews.filter(r => {
      const matchesFilter = this.currentFilter === 'all' || r.tags.includes(this.currentFilter);
      const visible = !this.shouldHideContent(r.content);
      return matchesFilter && visible;
    }).length;
  }

  async fetchReviewsPage(page = 1) {
    try {
      const limit = this.apiPageSize; // Always fetch in batches of 20
      let apiUrl = `${this.nectorConfig.baseUrl}?reference_product_source=shopify&reference_product_id=${this.productId}&page=${page}&limit=${limit}`;

      if (this.currentSort) {
        if (this.currentSort === 'top_rated') {
          apiUrl += `&sort=rating&direction=desc`;
        } else if (this.currentSort === 'image_first') {
          apiUrl += `&sort=image_count&direction=desc`;
        }
      }

      console.log(`🌐 Fetching API page ${page}: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'x-apikey': this.nectorConfig.apiKey,
          'x-source': 'web',
          'x-workspaceid': this.nectorConfig.workspaceId,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Nector API returned ${response.status}: ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      console.error(`❌ Error fetching page ${page}:`, error);
      throw error;
    }
  }

  formatNectorReview(review, index) {
    // console.log(`🔧 Formatting review ${index}:`, review);

    // Extract rating (try multiple field names)
    let rating = 5; // default
    if (review.rating !== undefined) rating = review.rating;
    else if (review.stars !== undefined) rating = review.stars;
    else if (review.score !== undefined) rating = review.score;
    else if (review.star_rating !== undefined) rating = review.star_rating;

    // Extract author name (try multiple field names)
    let author = 'Anonymous';
    if (review.customer_name) author = review.customer_name;
    else if (review.reviewer_name) author = review.reviewer_name;
    else if (review.name) author = review.name;
    else if (review.author) author = review.author;
    else if (review.customer?.name) author = review.customer.name;
    else if (review.user?.name) author = review.user.name;
    else if (review.reviewer?.name) author = review.reviewer.name;

    // Extract review content (try multiple field names)
    let content = '';
    if (review.review_text) content = review.review_text;
    else if (review.content) content = review.content;
    else if (review.text) content = review.text;
    else if (review.comment) content = review.comment;
    else if (review.body) content = review.body;
    else if (review.description) content = review.description;
    else if (review.message) content = review.message;

    // Extract date (try multiple field names)
    let dateString = new Date().toISOString();
    if (review.created_at) dateString = review.created_at;
    else if (review.date) dateString = review.date;
    else if (review.review_date) dateString = review.review_date;
    else if (review.submitted_at) dateString = review.submitted_at;
    else if (review.createdAt) dateString = review.createdAt;

    // Extract images from uploads / other sources
    const uploadArray = Array.isArray(review.uploads)
      ? review.uploads
      : Array.isArray(review.uploads?.items)
        ? review.uploads.items
        : Array.isArray(review.uploads?.data)
          ? review.uploads.data
          : [];

    const imagesFromUploads = uploadArray.reduce((acc, upload) => {
      const link = upload?.link || upload?.url || upload?.image_url;
      if (link) acc.push(link);
      return acc;
    }, []);

    const fallbackImages = this.extractNectorImages(review);
    const images = [...new Set([...imagesFromUploads, ...fallbackImages])];

    // Check if verified (try multiple field names)
    let verified = false;
    if (review.verified_buyer === true) verified = true;
    else if (review.verified === true) verified = true;
    else if (review.is_verified === true) verified = true;
    else if (review.verifiedBuyer === true) verified = true;

    const formatted = {
      id: review.id || review._id || review.review_id || `review-${index}`,
      rating: parseFloat(rating),
      author: author,
      date: this.formatDate(dateString),
      content: content,
      verified: verified,
      images: images,
      tags: this.extractTags(content)
    };

    return formatted;
  }

  extractNectorImages(review) {
    const images = [];

    // Check various possible image field names
    const imageSources = [
      review.images,
      review.photos,
      review.media,
      review.attachments,
      review.picture_urls,
      review.images_url,
      review.image_urls,
      review.imageUrls
    ];

    imageSources.forEach(source => {
      if (!source) return;

      if (Array.isArray(source)) {
        source.forEach(item => {
          if (typeof item === 'string') {
            images.push(item);
          } else if (item?.url) {
            images.push(item.url);
          } else if (item?.image_url) {
            images.push(item.image_url);
          } else if (item?.src) {
            images.push(item.src);
          }
        });
      }
    });

    return images;
  }

  formatDate(dateString) {
    if (!dateString) return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  extractTags(text) {
    if (!text) return [];

    const tags = [];
    const lowerText = text.toLowerCase();

    // Quality tags
    if (lowerText.match(/quality|well made|durable|good|excellent|perfect|great|sturdy/)) {
      tags.push('quality');
    }

    // Fit tags
    if (lowerText.match(/fit|size|perfectly|fits|comfortable|snug|loose|tight/)) {
      tags.push('fit');
    }

    // Fabric tags
    if (lowerText.match(/fabric|material|soft|comfortable|cloth|cotton|texture|feel/)) {
      tags.push('fabric');
    }

    return tags;
  }

  processReviews() {
    console.log('🔄 Processing reviews stats...');

    // Use API stats if available, otherwise fallback to local calculation (which might be partial)
    let totalReviews = this.totalApiReviews;
    let avgRating = 0;
    let ratingCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    if (this.apiStats) {
      console.log('✅ Using API stats');
      // Calculate average from stats if not explicitly provided
      // stats array: [{rating: 5, count: 10}, ...]
      let sum = 0;
      let count = 0;

      if (Array.isArray(this.apiStats)) {
        this.apiStats.forEach(stat => {
          const r = parseInt(stat.rating);
          const c = parseInt(stat.count);
          if (!isNaN(r) && !isNaN(c)) {
            ratingCounts[r] = c;
            sum += r * c;
            count += c;
          }
        });
      }

      if (count > 0) {
        avgRating = sum / count;
        totalReviews = count; // Ensure total matches stats
      }
    } else {
      console.warn('⚠️ API stats not available, using local data (might be partial)');
      if (this.allReviews.length > 0) {
        totalReviews = this.allReviews.length; // This is just what we fetched
        avgRating = this.allReviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews;
        this.allReviews.forEach(review => {
          const rounded = Math.round(review.rating);
          if (ratingCounts.hasOwnProperty(rounded)) {
            ratingCounts[rounded]++;
          }
        });
      }
    }

    // Update summary elements
    const reviewsCountEl = document.getElementById('reviews-count');
    const avgRatingEl = document.getElementById('average-rating');

    if (reviewsCountEl) {
      reviewsCountEl.textContent = `Based on ${totalReviews} Review${totalReviews !== 1 ? 's' : ''}`;
    }

    if (avgRatingEl) {
      avgRatingEl.textContent = avgRating.toFixed(1);
    }

    // Update star rating
    const summaryStarsEl = document.getElementById('summary-stars');
    if (summaryStarsEl) {
      this.renderStars(summaryStarsEl, avgRating);
    }

    // Update rating bars
    Object.keys(ratingCounts).forEach(rating => {
      const count = ratingCounts[rating];
      // Percentage based on TOTAL reviews
      const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
      const bar = document.querySelector(`.rating-bar-fill[data-star="${rating}"]`);
      const countEl = document.querySelector(`.rating-count[data-star-count="${rating}"]`);

      if (bar) {
        bar.style.width = `${percentage}%`;
      }
      if (countEl) {
        countEl.textContent = count;
        if (count === 0) {
          countEl.classList.add('disabled');
        } else {
          countEl.classList.remove('disabled');
        }
      }
    });

    // Update tag counts - this we can only do based on what we have fetched, 
    // unless API provides tag stats. Assuming it doesn't for now.
    const tagCounts = { quality: 0, fit: 0, fabric: 0 };
    this.allReviews.forEach(review => {
      review.tags.forEach(tag => {
        if (tagCounts.hasOwnProperty(tag)) {
          tagCounts[tag]++;
        }
      });
    });

    const qualityCountEl = document.getElementById('quality-count');
    const fitCountEl = document.getElementById('fit-count');
    const fabricCountEl = document.getElementById('fabric-count');

    if (qualityCountEl) qualityCountEl.textContent = tagCounts.quality;
    if (fitCountEl) fitCountEl.textContent = tagCounts.fit;
    if (fabricCountEl) fabricCountEl.textContent = tagCounts.fabric;
  }

  handleFilterChange(filter) {
    console.log('🔍 Filter changed to:', filter);
    this.currentFilter = filter;
    this.currentPage = 1;

    // Update active state
    document.querySelectorAll('.filter-tag').forEach(tag => {
      tag.classList.toggle('active', tag.dataset.filter === filter);
    });

    // We need to ensure we have enough reviews for page 1 with the new filter
    this.ensureReviewsForPage(1);
  }

  renderReviews() {
    console.log('🎨 Rendering reviews...');
    const container = document.getElementById('reviews-list');

    if (!container) {
      console.error('❌ Could not find #reviews-list container');
      return;
    }

    // Filter and hide content
    const displayable = this.allReviews.filter(r => {
      const matchesFilter = this.currentFilter === 'all' || r.tags.includes(this.currentFilter);
      const visible = !this.shouldHideContent(r.content);
      return matchesFilter && visible;
    });

    if (this.currentSort === 'image_first') {
      displayable.sort((a, b) => {
        const aHasImages = a.images && a.images.length > 0;
        const bHasImages = b.images && b.images.length > 0;
        if (aHasImages === bHasImages) return 0;
        return bHasImages ? 1 : -1;
      });
    }

    this._effectiveFiltered = displayable;

    if (displayable.length === 0) {
      container.innerHTML = '<div class="no-reviews">No reviews match this filter.</div>';
      document.getElementById('pagination').innerHTML = '';
      return;
    }

    // Calculate pagination based on displayable reviews
    const startIdx = (this.currentPage - 1) * this.reviewsPerPage;
    const endIdx = startIdx + this.reviewsPerPage;
    const paginatedReviews = displayable.slice(startIdx, endIdx);

    console.log(`📄 Rendering page ${this.currentPage}: reviews ${startIdx}-${endIdx} (of ${displayable.length})`);

    // Clear container
    container.innerHTML = '';

    // Render reviews
    paginatedReviews.forEach((review, index) => {
      const reviewEl = this.createReviewElement(review);
      if (reviewEl) {
        container.appendChild(reviewEl);
      }
    });

    // Render pagination
    this.renderPagination();
  }

  openSortMenu() {
    const host = document.querySelector('.reviews-right');
    if (!host) return;
    let menu = host.querySelector('.filter-menu');
    if (!menu) {
      menu = document.createElement('div');
      menu.className = 'filter-menu';
      host.appendChild(menu);
    }
    const options = [
      { key: 'most_recent', label: 'Most Recent' },
      { key: 'top_rated', label: 'Top Rated' },
      { key: 'image_first', label: 'Image First' },
      { key: 'video_first', label: 'Video First' }
    ];
    menu.innerHTML = options.map(o => `
        <button class="filter-option ${this.currentSort === o.key ? 'active' : ''}" data-sort="${o.key}">${o.label}</button>
      `).join('');
    menu.querySelectorAll('.filter-option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const sort = e.currentTarget.dataset.sort;
        this.handleSortChange(sort);
        menu.classList.remove('open');
      });
    });
    // position near the filter icon
    const toggle = document.querySelector('.filter-toggle');
    if (toggle) {
      const hostRect = host.getBoundingClientRect();
      const tRect = toggle.getBoundingClientRect();
      menu.style.width = window.innerWidth >= 1024 ? '220px' : '180px';
      menu.classList.add('open');
      const mRect = menu.getBoundingClientRect();
      const top = tRect.bottom - hostRect.top + 8;
      const left = tRect.right - hostRect.left - mRect.width;
      menu.style.top = `${Math.max(0, top)}px`;
      menu.style.left = `${Math.max(0, left)}px`;
    } else {
      menu.classList.add('open');
    }
  }

  handleSortChange(sort) {
    this.currentSort = sort;
    this.currentPage = 1;

    // Reset fetch state because sort order changed
    this.allReviews = [];
    this.nextApiPageToFetch = 1;

    this.ensureReviewsForPage(1);
  }

  applySort(reviews) {
    // No longer needed as we fetch sorted from API or rely on API order
    return reviews;
  }

  createReviewElement(review) {
    const div = document.createElement('div');
    div.className = 'review-item';

    const starsHTML = this.getStarsHTML(review.rating);
    const verifiedBadge = review.verified ? `
        <div class="verified-badge">
          <img src="https://cdn.shopify.com/s/files/1/0678/4172/0509/files/checkmark-badge-01.png?v=1764766511" alt="Verified" class="verified-icon">
        </div>
      ` : '';

    const hasImages = Array.isArray(review.images) && review.images.length > 0;
    const imagesHTML = hasImages ? `
        <div class="review-images">
          ${review.images.map(img => `
            <div class="review-image">
              <img src="${img}" alt="Review image" loading="lazy" onerror="this.parentElement.style.display='none'">
            </div>
          `).join('')}
        </div>
      ` : '';

    const hideContent = this.shouldHideContent(review.content);
    if (hideContent) {
      return null;
    }
    const safeContent = this.escapeHtml(review.content);

    div.innerHTML = `
        <div class="review-header">
          <div class="review-stars-wrapper">
            ${starsHTML}
          </div>
          <div class="review-date">${review.date}</div>
        </div>
        ${hideContent ? '' : `
          <div class="review-author">
            <span class="review-author-name">${this.escapeHtml(review.author)}</span>
            ${verifiedBadge}
          </div>
        `}
        ${hideContent ? '' : `<div class="review-content">${safeContent}</div>`}
        ${imagesHTML}
      `;

    const imgEls = div.querySelectorAll('.review-image img');
    imgEls.forEach(img => {
      img.addEventListener('click', () => {
        this.openImageModal(review, img.src);
      });
    });

    return div;
  }

  shouldHideContent(text) {
    if (!text) return true;
    const t = String(text).trim();
    if (t.length === 0) return true;
    // Hide comments that are only punctuation/placeholder dots or ellipses
    // Covers '.', '..', '...', '•', '-', '_', '*', spaces
    const onlyPunct = /^[\s\.\-_*•…]+$/u.test(t);
    if (onlyPunct) return true;
    // Also hide extremely short comments like single emoji or one character without letters/digits
    const hasAlnum = /[a-zA-Z0-9]/.test(t);
    return !hasAlnum && t.length <= 3;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getStarsHTML(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const fullStarUrl = 'https://cdn.shopify.com/s/files/1/0678/4172/0509/files/Vector_deaa4ad5-70d4-4c49-930b-2a2811329a98.png?v=1764579650';
    const halfStarUrl = 'https://cdn.shopify.com/s/files/1/0678/4172/0509/files/star-half.png?v=1764773071';
    const emptyStarUrl = 'https://cdn.shopify.com/s/files/1/0678/4172/0509/files/elements.png?v=1764579649';

    let html = '<div class="stars">';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      html += `<img class="star full" src="${fullStarUrl}" alt="Full Star" width="29" height="29">`;
    }

    // Half star
    if (hasHalfStar) {
      html += `<img class="star half" src="${halfStarUrl}" alt="Half Star" width="29" height="29">`;
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      html += `<img class="star empty" src="${emptyStarUrl}" alt="Empty Star" width="29" height="29">`;
    }

    html += '</div>';
    return html;
  }

  renderStars(container, rating) {
    if (!container) return;
    container.innerHTML = this.getStarsHTML(rating);
  }

  renderPagination() {
    const container = document.getElementById('pagination');
    if (!container) return;

    // Use totalApiReviews from server for pagination if available
    // Otherwise fallback to what we have (which might be partial)
    const totalCount = this.totalApiReviews > 0 ? this.totalApiReviews : this._effectiveFiltered.length;
    const totalPages = Math.ceil(totalCount / this.reviewsPerPage);

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '';

    // No previous button (match desired UI)

    // Page numbers
    const maxVisible = 3;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      html += `
          <button class="pagination-btn ${i === this.currentPage ? 'active' : ''}" 
            onclick="customerReviews.goToPage(${i})">
            ${i}
          </button>
        `;
    }

    // Next button (SVG chevron)
    html += `
        <button class="pagination-btn arrow" ${this.currentPage === totalPages ? 'disabled' : ''} 
          onclick="customerReviews.goToPage(${this.currentPage + 1})" aria-label="Next page">
          <svg class="pagination-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 5L13 10L7 15" stroke="#5E3023" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      `;

    // Last page button (SVG chevron + end bar)
    html += `
        <button class="pagination-btn arrow" ${this.currentPage === totalPages ? 'disabled' : ''} 
          onclick="customerReviews.goToPage(${totalPages})" aria-label="Last page">
          <svg class="pagination-arrow" width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 5L12 10L6 15" stroke="#5E3023" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="18" y1="4" x2="18" y2="16" stroke="#5E3023" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      `;

    container.innerHTML = html;
  }

  openImageModal(review, imageUrl) {
    let overlay = document.querySelector('.review-modal-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'review-modal-overlay';
      document.body.appendChild(overlay);
    }
    const images = review.images || [imageUrl];
    let currentIndex = images.indexOf(imageUrl);
    if (currentIndex === -1) currentIndex = 0;
    const hasMultiple = images.length > 1;

    const starsHTML = this.getStarsHTML(review.rating);
    overlay.innerHTML = `
        <div class="review-modal">
          <button class="review-modal-close" aria-label="Close">×</button>
          <div class="review-modal-image">
            ${hasMultiple ? `<button class="modal-nav prev" aria-label="Previous image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>` : ''}
            <img src="${images[currentIndex]}" alt="Review image" />
            ${hasMultiple ? `<button class="modal-nav next" aria-label="Next image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>` : ''}
          </div>
          <div class="review-modal-details">
            <div class="review-modal-author">${this.escapeHtml(review.author)}</div>
            <div class="review-modal-stars">${starsHTML}</div>
            <div class="review-modal-content">${this.escapeHtml(review.content)}</div>
            <div class="review-modal-date">${review.date}</div>
          </div>
        </div>
      `;
    overlay.classList.add('open');

    const closeBtn = overlay.querySelector('.review-modal-close');
    closeBtn.addEventListener('click', () => this.closeImageModal());

    if (hasMultiple) {
      const imgEl = overlay.querySelector('.review-modal-image img');
      const prevBtn = overlay.querySelector('.modal-nav.prev');
      const nextBtn = overlay.querySelector('.modal-nav.next');

      const updateImage = () => {
        imgEl.src = images[currentIndex];
      };

      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        currentIndex = (currentIndex - 1 + images.length) % images.length;
        updateImage();
      });

      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        currentIndex = (currentIndex + 1) % images.length;
        updateImage();
      });
    }

    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeImageModal(); });
    document.addEventListener('keydown', this._modalEscHandler = (e) => { if (e.key === 'Escape') this.closeImageModal(); });
  }

  closeImageModal() {
    const overlay = document.querySelector('.review-modal-overlay');
    if (overlay) overlay.classList.remove('open');
    if (this._modalEscHandler) {
      this._modalEscHandler = null;
    }
  }

  async goToPage(page) {
    // Use totalApiReviews from server for pagination if available
    // Otherwise fallback to what we have (which might be partial)
    const totalCount = this.totalApiReviews > 0 ? this.totalApiReviews : this._effectiveFiltered.length;
    const totalPages = Math.ceil(totalCount / this.reviewsPerPage);

    if (page < 1 || page > totalPages) return;

    this.currentPage = page;

    // Ensure we have reviews for this page
    try {
      this.showLoading();
      await this.ensureReviewsForPage(page);
      this.hideLoading();
    } catch (e) {
      console.error(`❌ Error going to page ${page}:`, e);
      this.hideLoading();
    }

    // Scroll to top of reviews
    const reviewsSection = document.querySelector('.reviews-right');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  openReviewForm() {
    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Helper function to trigger click on mobile
    const triggerClick = (element) => {
      if (!element) return false;

      // Make element visible and accessible
      const prevDisplay = element.style.display;
      const prevVisibility = element.style.visibility;
      const prevPointerEvents = element.style.pointerEvents;
      const prevOpacity = element.style.opacity;

      element.style.display = 'block';
      element.style.visibility = 'visible';
      element.style.pointerEvents = 'auto';
      element.style.opacity = '1';
      element.removeAttribute('disabled');

      if (isMobile) {
        // For mobile, use multiple methods to ensure click works
        element.scrollIntoView({ behavior: 'auto', block: 'center' });

        // Force element to be interactive
        element.setAttribute('tabindex', '0');

        // Try focus first
        try {
          element.focus();
        } catch (e) {
          console.log('Could not focus element:', e);
        }

        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          // Try multiple click methods
          try {
            element.click();
          } catch (e) {
            console.log('Standard click failed:', e);
          }

          // Try MouseEvent
          try {
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
              detail: 1,
              buttons: 1
            });
            element.dispatchEvent(clickEvent);
          } catch (e) {
            console.log('MouseEvent click failed:', e);
          }

          // Try mousedown + mouseup (more reliable on mobile)
          try {
            const mouseDown = new MouseEvent('mousedown', {
              bubbles: true,
              cancelable: true,
              view: window,
              buttons: 1
            });
            const mouseUp = new MouseEvent('mouseup', {
              bubbles: true,
              cancelable: true,
              view: window,
              buttons: 1
            });
            element.dispatchEvent(mouseDown);
            setTimeout(() => {
              element.dispatchEvent(mouseUp);
            }, 10);
          } catch (e) {
            console.log('Mouse down/up events failed:', e);
          }

          // Restore styles after a delay
          setTimeout(() => {
            element.style.display = prevDisplay;
            element.style.visibility = prevVisibility;
            element.style.pointerEvents = prevPointerEvents;
            element.style.opacity = prevOpacity;
            element.removeAttribute('tabindex');
          }, 1000);
        }, 100);
      } else {
        element.click();
        // Restore styles
        setTimeout(() => {
          element.style.display = prevDisplay;
          element.style.visibility = prevVisibility;
          element.style.pointerEvents = prevPointerEvents;
          element.style.opacity = prevOpacity;
        }, 500);
      }

      return true;
    };

    const attempt = () => {
      // Try window.nectorOpenReviewModal first (most reliable)
      if (typeof window.nectorOpenReviewModal === 'function') {
        try {
          const result = window.nectorOpenReviewModal();
          if (result) {
            console.log('✅ Opened review modal via window.nectorOpenReviewModal');
            return true;
          }
        } catch (e) {
          console.warn('⚠️ Error calling window.nectorOpenReviewModal:', e);
        }
      }

      // Try Nector reviews host element with shadow DOM - get it directly
      const host = document.getElementById('nector-reviews');
      if (host) {
        let shadowRoot = host.shadowRoot;

        // If shadow root doesn't exist, wait a bit (will retry)
        if (!shadowRoot) {
          console.log('⏳ Shadow root not ready, will retry...');
          return false;
        }

        // Search more thoroughly in shadow DOM
        const allClickable = Array.from(shadowRoot.querySelectorAll('*')).filter(el => {
          const tag = el.tagName?.toLowerCase();
          return tag === 'button' ||
            tag === 'a' ||
            el.getAttribute('role') === 'button' ||
            el.onclick !== null ||
            el.hasAttribute('onclick') ||
            (el.style && el.style.cursor === 'pointer');
        });

        // Look for write review button with multiple criteria
        let writeBtn = allClickable.find(el => {
          const text = (el.textContent || el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase();
          const className = (el.className || '').toLowerCase();
          const id = (el.id || '').toLowerCase();

          return (text.includes('write') && (text.includes('review') || text.includes('a review'))) ||
            text.includes('leave a review') ||
            text.includes('submit review') ||
            text.includes('add review') ||
            className.includes('write') && className.includes('review') ||
            className.includes('review-form') ||
            id.includes('write') && id.includes('review');
        });

        // If not found, try to find any button that might open a modal
        if (!writeBtn) {
          writeBtn = allClickable.find(el => {
            const text = (el.textContent || '').toLowerCase();
            return text.includes('review') && (text.includes('write') || text.includes('add') || text.includes('submit'));
          });
        }

        if (writeBtn) {
          console.log('✅ Found write review button in shadow DOM:', writeBtn);
          return triggerClick(writeBtn);
        } else {
          console.log('⚠️ Write review button not found in shadow DOM. Available buttons:',
            allClickable.map(el => el.textContent?.substring(0, 30)).filter(Boolean));
        }
      }

      // Try Nector global object methods
      try {
        const nector = window.nector || window.Nector || window.NECTOR;
        if (nector) {
          if (typeof nector.openReviewForm === 'function') {
            nector.openReviewForm();
            console.log('✅ Opened via nector.openReviewForm');
            return true;
          }
          if (typeof nector.openReviewModal === 'function') {
            nector.openReviewModal();
            console.log('✅ Opened via nector.openReviewModal');
            return true;
          }
          if (typeof nector.openReview === 'function') {
            nector.openReview();
            console.log('✅ Opened via nector.openReview');
            return true;
          }
          // Try any method that might open a review form
          const methods = Object.keys(nector).filter(key =>
            typeof nector[key] === 'function' &&
            (key.toLowerCase().includes('review') || key.toLowerCase().includes('modal') || key.toLowerCase().includes('form'))
          );
          for (const method of methods) {
            try {
              nector[method]();
              console.log(`✅ Tried nector.${method}()`);
              return true;
            } catch (e) {
              // Continue to next method
            }
          }
        }
      } catch (e) {
        console.warn('⚠️ Error accessing Nector object:', e);
      }

      // Try to find any hidden modal trigger buttons in the page
      const hiddenButtons = Array.from(document.querySelectorAll('button, a, [role="button"]')).filter(el => {
        const style = getComputedStyle(el);
        return (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') &&
          (el.textContent?.toLowerCase().includes('review') ||
            el.getAttribute('aria-label')?.toLowerCase().includes('review') ||
            el.className?.toLowerCase().includes('review'));
      });

      if (hiddenButtons.length > 0) {
        console.log(`🔍 Found ${hiddenButtons.length} hidden review buttons, trying first one...`);
        return triggerClick(hiddenButtons[0]);
      }

      // Try all shadow hosts (more thorough search)
      const shadowHosts = Array.from(document.querySelectorAll('*')).filter(el => el.shadowRoot);
      for (let i = 0; i < shadowHosts.length; i++) {
        const root = shadowHosts[i].shadowRoot;
        const allElements = Array.from(root.querySelectorAll('*'));
        const btns = allElements.filter(el => {
          const tag = el.tagName?.toLowerCase();
          return tag === 'button' || tag === 'a' || el.getAttribute('role') === 'button' || el.onclick !== null;
        });

        const b = btns.find(el => {
          const t = (el.textContent || el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase();
          return (t.includes('write') && t.includes('review')) ||
            t.includes('leave a review') ||
            t.includes('submit review') ||
            t.includes('add review');
        });

        if (b) {
          console.log('✅ Found write review button in shadow host');
          return triggerClick(b);
        }
      }

      // Try Judge.me fallback
      const jdgmRoot = document.querySelector('#judgeme_product_reviews, .jdgm-widget.jdgm-review-widget');
      if (jdgmRoot) {
        const els = Array.from(jdgmRoot.querySelectorAll('button, a'));
        const btn = els.find(el => {
          const t = (el.textContent || '').toLowerCase();
          return t.includes('write') && t.includes('review');
        }) || jdgmRoot.querySelector('.jdgm-write-rev, .jdgm-btn--border');
        if (btn) {
          jdgmRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return triggerClick(btn);
        }
      }

      // Try data attributes and class selectors
      const fallbackBtn = document.querySelector('[data-nector-review-form], .nector-write-review, #nector-review-button, [data-review-form]');
      if (fallbackBtn) {
        return triggerClick(fallbackBtn);
      }

      // Last resort: search all buttons/links
      const anyWriteBtn = Array.from(document.querySelectorAll('button, a, [role="button"]'))
        .find(el => {
          const t = (el.textContent || el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase();
          return (t.includes('write') && t.includes('review')) ||
            t.includes('leave a review');
        });
      if (anyWriteBtn) {
        return triggerClick(anyWriteBtn);
      }

      return false;
    };

    // Immediate attempt
    if (attempt()) {
      console.log('✅ Review form opened on first attempt');
      return;
    }

    // For mobile, also try waiting for shadow DOM to be ready using MutationObserver
    if (isMobile) {
      const host = document.getElementById('nector-reviews');
      if (host && !host.shadowRoot) {
        console.log('⏳ Setting up MutationObserver for shadow root on mobile...');
        const observer = new MutationObserver((mutations, obs) => {
          if (host.shadowRoot) {
            console.log('✅ Shadow root detected, attempting to open modal...');
            obs.disconnect();
            setTimeout(() => {
              if (attempt()) {
                console.log('✅ Review form opened after shadow root appeared');
              }
            }, 200);
          }
        });

        observer.observe(host, {
          childList: true,
          subtree: true,
          attributes: true
        });

        // Disconnect after 10 seconds
        setTimeout(() => observer.disconnect(), 10000);
      }
    }

    // Retry with increasing delays (longer delays for mobile)
    const delays = isMobile ? [500, 1000, 2000, 3500, 5000, 8000] : [300, 800, 1600, 3000, 5000];
    delays.forEach((delay, index) => {
      setTimeout(() => {
        if (attempt()) {
          console.log(`✅ Review form opened on attempt ${index + 2}`);
        } else if (index === delays.length - 1) {
          console.error('❌ Failed to open review form after all attempts');
        }
      }, delay);
    });
  }

  showLoading() {
    const container = document.getElementById('reviews-list');
    if (container) {
      container.innerHTML = '<div class="loading">Loading reviews</div>';
    }
  }

  hideLoading() {
    // Loading will be replaced by actual content
  }

  showNoReviews() {
    console.log('ℹ️ Showing no reviews message');
    const container = document.getElementById('reviews-list');
    if (container) {
      container.innerHTML = '<div class="no-reviews">No reviews yet. Be the first to review!</div>';
    }

    const reviewsCountEl = document.getElementById('reviews-count');
    const avgRatingEl = document.getElementById('average-rating');

    if (reviewsCountEl) reviewsCountEl.textContent = 'Based on 0 Reviews';
    if (avgRatingEl) avgRatingEl.textContent = '0.0';
  }

  showError(message) {
    console.error('💥 Showing error message:', message);
    const container = document.getElementById('reviews-list');
    if (container) {
      container.innerHTML = `
          <div class="error-message" style="text-align: center; padding: 40px; color: #999;">
            <p>Unable to load reviews at this time.</p>
            <p style="font-size: 14px; margin-top: 10px;">${this.escapeHtml(message)}</p>
          </div>
        `;
    }
    this.showNoReviews();
  }
}

// Initialize when DOM is ready
console.log('🚀 Customer Reviews script loaded');

if (!window.customerReviews) {
  const init = () => {
    if (window.customerReviews) return;
    console.log('📄 Initializing CustomerReviews');
    window.customerReviews = new CustomerReviews();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

